import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Autocomplete } from '@mui/material';
import { useReducer } from "react";
import {
  OperatingSystem,
  Software,
  SoftwareVersion,
} from "../../api/api-generated";
import {
  useFindAllOperatingSystemsQuery,
  useFindAllSoftwareVersionsForUserQuery,
} from "../../api/api-hooks";
import LinkNewTab from "../../components/LinkNewTab";
import useStyles from "./softwareDisplayStyles";

interface Props {
  softwareType: Software;
}

interface DownloadFormType {
  operatingSystem: OperatingSystem | null | undefined;
  softwareVersion: SoftwareVersion | null | undefined;
}

const initialFormState: DownloadFormType = {
  operatingSystem: null,
  softwareVersion: null,
};

const VersionDownloadDisplay = ({ softwareType }: Props) => {
  const classes = useStyles();
  const { data: allVersions } = useFindAllSoftwareVersionsForUserQuery({});
  const { data: operatingSystems } = useFindAllOperatingSystemsQuery({});
  const [formState, setFormState] = useReducer(
    (curVals: any, newVals: any) => ({ ...curVals, ...newVals }),
    initialFormState
  );
  if (allVersions === undefined || operatingSystems === undefined)
    return <CircularProgress></CircularProgress>;
  const filteredVersions = allVersions.filter((version) => {
    if (!formState.operatingSystem) return false;
    return (
      version.softwareId === softwareType.id &&
      version.operatingSystemsId === formState.operatingSystem.id
    );
  });
  const handleFormChange = (
    event: any,
    autoboxValue: object,
    label: string
  ) => {
    if (label === "operatingSystem" && autoboxValue === null) {
      setFormState({
        operatingSystem: null,
        softwareVersion: null,
      });
    } else if (label === "operatingSystem") {
      setFormState({
        softwareVersion: null,
        operatingSystem: autoboxValue,
      });
    } else {
      setFormState({
        softwareVersion: autoboxValue,
      });
    }
  };

  return (
    <Grid
      item
      container
      direction="column"
      alignItems="flex-start"
      spacing={2}
      className={classes.formContainer}
    >
      <Grid item>
        <Typography variant="h5">Download Versions</Typography>
      </Grid>
      <Grid item>
        <Autocomplete
          onChange={(event, autoboxValue) => {
            handleFormChange(event, autoboxValue, "operatingSystem");
          }}
          value={formState.operatingSystem}
          options={operatingSystems}
          getOptionLabel={(os) => os.osName}
          style={{ width: "300px" }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Operating System"
              variant="outlined"
            />
          )}
        />
      </Grid>
      <Grid item>
        <Autocomplete
          onChange={(event, autoboxValue) => {
            handleFormChange(event, autoboxValue, "softwareVersion");
          }}
          value={formState.softwareVersion}
          options={filteredVersions}
          getOptionLabel={(version) =>
            `${version.versionNumber} ${
              version.versionType ? version.versionType : ""
            }`
          }
          style={{ width: "300px" }}
          renderInput={(params) => (
            <TextField {...params} label="Select Version" variant="outlined" />
          )}
          disabled={!formState.operatingSystem}
        />
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          color="primary"
          disabled={!formState.softwareVersion}
          target="_blank"
          rel="noopener noreferrer"
          href={formState.softwareVersion ? formState.softwareVersion.link : ""}
        >
          Download
        </Button>
        {formState.softwareVersion && (
          <>
            {formState.softwareVersion.changelog && (
              <Button color="primary">
                <LinkNewTab
                  href={formState.softwareVersion.changelog}
                  label="Changelog"
                />
              </Button>
            )}
            {formState.softwareVersion.userguideLink && (
              <Button color="primary">
                <LinkNewTab
                  href={formState.softwareVersion.userguideLink}
                  label="User Guide"
                />
              </Button>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default VersionDownloadDisplay;
